import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { compressImage, humanBytes, savingsLabel } from "@/lib/imageCompress";
import { POLICIES, defaultPolicy } from "@/lib/policies";
import { Paintbrush, Upload, Camera, ShoppingBag, Loader2, Link as LinkIcon, ExternalLink, CheckCircle2, XCircle, MapPin, Instagram, Facebook, Mail, Type, Sun, Moon, MonitorSmartphone, Sparkles, Image as ImageIcon, FileText } from "lucide-react";

const PRESETS = [
  "#0055FF", "#FF5000", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#0F172A",
];
const ACCENT_PRESETS = [
  "#FF5000", "#F59E0B", "#EC4899", "#8B5CF6", "#06B6D4", "#22C55E", "#0F172A",
];
// Font choices are a small, curated set of modern web-safe stacks so the
// storefront actually reflects the picked personality (no arbitrary user
// font names — that'd be a security & rendering minefield).
const FONT_OPTIONS = [
  { id: "default", label: "System (default)", family: "inherit" },
  { id: "inter", label: "Inter · clean & neutral", family: "'Inter', system-ui, sans-serif" },
  { id: "manrope", label: "Manrope · modern round", family: "'Manrope', system-ui, sans-serif" },
  { id: "space", label: "Space Grotesk · techy", family: "'Space Grotesk', system-ui, sans-serif" },
  { id: "dm", label: "DM Sans · friendly", family: "'DM Sans', system-ui, sans-serif" },
  { id: "poppins", label: "Poppins · lifestyle", family: "'Poppins', system-ui, sans-serif" },
  { id: "playfair", label: "Playfair Display · premium", family: "'Playfair Display', Georgia, serif" },
];
const THEME_OPTIONS = [
  { id: "light", label: "Light", icon: Sun },
  { id: "dark", label: "Dark", icon: Moon },
  { id: "auto", label: "Auto (device)", icon: MonitorSmartphone },
];

export default function Branding() {
  const [form, setForm] = useState({
    shop_name: "",
    tagline: "",
    logo_url: "",
    primary_color: "#0055FF",
    accent_color: "#FF5000",
    slug: "",
    shop_lat: "",
    shop_lng: "",
    shop_address: "",
    hero_image_url: "",
    hero_headline: "",
    font_family: "default",
    theme_mode: "light",
    instagram_url: "",
    facebook_url: "",
    whatsapp_number: "",
    support_email: "",
    show_powered_by: true,
    legal_name: "",
    support_phone: "",
    gstin: "",
    refund_window_days: "",
    refund_processing_days: "",
    delivery_time: "",
    policy_terms: "",
    policy_refund: "",
    policy_shipping: "",
    policy_privacy: "",
  });
  const [editPolicy, setEditPolicy] = useState(null); // which policy's custom text is open
  const [savedSlug, setSavedSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      // IMPORTANT: hit the *authenticated* /mine endpoint. The public
      // /store/brand falls back to the default tenant when no slug is
      // provided, which caused non-default admins to see (and appear to
      // silently overwrite) the wrong tenant's branding data.
      const { data } = await api.get("/store/brand/mine");
      if (data && Object.keys(data).length) {
        setForm((f) => ({
          ...f,
          shop_name: data.shop_name || "",
          tagline: data.tagline || "",
          logo_url: data.logo_url || "",
          primary_color: data.primary_color || "#0055FF",
          accent_color: data.accent_color || "#FF5000",
          slug: data.slug || "",
          shop_lat: data.shop_lat ?? "",
          shop_lng: data.shop_lng ?? "",
          shop_address: data.shop_address || "",
          hero_image_url: data.hero_image_url || "",
          hero_headline: data.hero_headline || "",
          font_family: data.font_family || "default",
          theme_mode: data.theme_mode || "light",
          instagram_url: data.instagram_url || "",
          facebook_url: data.facebook_url || "",
          whatsapp_number: data.whatsapp_number || "",
          support_email: data.support_email || "",
          show_powered_by: data.show_powered_by ?? true,
          legal_name: data.legal_name || "",
          support_phone: data.support_phone || "",
          gstin: data.gstin || "",
          refund_window_days: data.refund_window_days ?? "",
          refund_processing_days: data.refund_processing_days ?? "",
          delivery_time: data.delivery_time || "",
          policy_terms: data.policy_terms || "",
          policy_refund: data.policy_refund || "",
          policy_shipping: data.policy_shipping || "",
          policy_privacy: data.policy_privacy || "",
        }));
        setSavedSlug(data.slug || "");
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Shared handler for logo + hero image uploads — compresses first so we
  // don't push 5-8 MB JPEGs from the owner's phone straight into Mongo.
  const uploadImage = async (e, field, maxDimension) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image must be under 15 MB");
      return;
    }
    try {
      const r = await compressImage(file, { maxDimension });
      setForm((f) => ({ ...f, [field]: r.dataUrl }));
      const s = savingsLabel(r.originalBytes, r.compressedBytes);
      toast.success(
        s
          ? `Uploaded — ${humanBytes(r.originalBytes)} → ${humanBytes(r.compressedBytes)} (${s})`
          : "Uploaded",
      );
    } catch (err) {
      toast.error("Could not read the image. Try again.");
    }
  };

  const uploadLogo = (e) => uploadImage(e, "logo_url", 512);
  const uploadHero = (e) => uploadImage(e, "hero_image_url", 1800);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Cast lat/lng to numbers, blank → null so the API can clear the value.
      payload.shop_lat = payload.shop_lat === "" ? null : Number(payload.shop_lat);
      payload.shop_lng = payload.shop_lng === "" ? null : Number(payload.shop_lng);
      for (const k of ["refund_window_days", "refund_processing_days"]) {
        payload[k] = payload[k] === "" ? null : Math.max(0, parseInt(payload[k], 10) || 0);
      }
      const { data } = await api.put("/store/brand", payload);
      setSavedSlug(data?.slug || "");
      toast.success("Storefront branding saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation isn't supported on this browser.");
      return;
    }
    toast("Requesting location…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          shop_lat: pos.coords.latitude.toFixed(6),
          shop_lng: pos.coords.longitude.toFixed(6),
        }));
        toast.success("Shop coordinates captured — save to apply.");
      },
      (err) => toast.error(err?.message || "Could not read location"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // Client-side slug preview — mirrors the backend regex so what the owner
  // sees below the input matches what will actually be stored.
  const slugPreview = (form.slug || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  const slugValid = slugPreview.length >= 3 && slugPreview.length <= 40;
  const slugChanged = slugPreview !== savedSlug;
  const publicUrl = savedSlug ? `${window.location.origin}/shop/${savedSlug}` : `${window.location.origin}/shop`;
  const careUrl = savedSlug ? `${window.location.origin}/care/${savedSlug}` : "";

  if (loading) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="owner-branding">
      <div className="mb-6">
        <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">Marketplace</div>
        <h1 className="font-heading text-3xl font-bold mt-2 flex items-center gap-2">
          <Paintbrush className="w-7 h-7" /> Storefront branding
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Customize how customers see your shop at <span className="mono">/shop</span>.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-5 space-y-4">
            <div className="space-y-2">
              <Label>Shop name</Label>
              <Input
                value={form.shop_name}
                onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
                placeholder="e.g. Bipra Computer Care"
                data-testid="brand-shop-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Tagline</Label>
              <Textarea
                rows={2}
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="e.g. Trusted computer parts, fast delivery"
                data-testid="brand-tagline"
              />
            </div>
            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center overflow-hidden">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" className="w-full h-full object-contain" />
                  ) : (
                    <ShoppingBag className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <label className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50 text-sm">
                  <Upload className="w-4 h-4" /> Upload
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="brand-logo-upload" />
                </label>
                <label className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50 text-sm" data-testid="brand-logo-camera-label">
                  <Camera className="w-4 h-4" /> Take photo
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadLogo} data-testid="brand-logo-camera" />
                </label>
                {form.logo_url && (
                  <button onClick={() => setForm({ ...form, logo_url: "" })} className="text-xs text-red-600 hover:underline" data-testid="brand-logo-clear">Remove</button>
                )}
              </div>
              <p className="text-[10px] text-slate-500">PNG/SVG, under 500 KB. Square works best.</p>
            </div>
            <div className="space-y-2">
              <Label>Primary color</Label>
              <div className="flex gap-1.5 flex-wrap items-center">
                {PRESETS.map((c) => (
                  <button
                    key={c}
                    className={"w-7 h-7 rounded-full border-2 " + (form.primary_color === c ? "border-slate-900 scale-110" : "border-slate-200")}
                    style={{ backgroundColor: c }}
                    onClick={() => setForm({ ...form, primary_color: c })}
                    data-testid={`brand-color-${c.slice(1)}`}
                  />
                ))}
                <Input
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="ml-2 w-24 mono text-xs"
                  data-testid="brand-color-hex"
                />
              </div>
              <p className="text-[10px] text-slate-500">Used for the header, primary buttons, and links.</p>
            </div>

            <div className="space-y-2">
              <Label>Accent color</Label>
              <div className="flex gap-1.5 flex-wrap items-center">
                {ACCENT_PRESETS.map((c) => (
                  <button
                    key={c}
                    className={"w-7 h-7 rounded-full border-2 " + (form.accent_color === c ? "border-slate-900 scale-110" : "border-slate-200")}
                    style={{ backgroundColor: c }}
                    onClick={() => setForm({ ...form, accent_color: c })}
                    data-testid={`brand-accent-${c.slice(1)}`}
                  />
                ))}
                <Input
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="ml-2 w-24 mono text-xs"
                  data-testid="brand-accent-hex"
                />
              </div>
              <p className="text-[10px] text-slate-500">Highlights price tags, sale badges, and discount pills.</p>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <Label className="flex items-center gap-1.5">
                <Type className="w-3.5 h-3.5" /> Storefront font
              </Label>
              <select
                value={form.font_family}
                onChange={(e) => setForm({ ...form, font_family: e.target.value })}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                data-testid="brand-font-select"
              >
                {FONT_OPTIONS.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
              <div
                className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-lg"
                style={{ fontFamily: FONT_OPTIONS.find((f) => f.id === form.font_family)?.family || "inherit" }}
                data-testid="brand-font-preview"
              >
                {form.shop_name || "Your Shop Name"} · looks like this
              </div>
            </div>

            <div className="space-y-2">
              <Label>Theme mode</Label>
              <div className="grid grid-cols-3 gap-2">
                {THEME_OPTIONS.map((t) => {
                  const Icon = t.icon;
                  const active = form.theme_mode === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm({ ...form, theme_mode: t.id })}
                      className={
                        "flex flex-col items-center justify-center gap-1 h-16 rounded-md border-2 transition " +
                        (active ? "border-[#0055FF] bg-[#0055FF]/5" : "border-slate-200 hover:border-slate-300")
                      }
                      data-testid={`brand-theme-${t.id}`}
                    >
                      <Icon className={"w-4 h-4 " + (active ? "text-[#0055FF]" : "text-slate-500")} />
                      <span className={"text-xs " + (active ? "font-semibold" : "")}>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <Label className="flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" /> Hero banner (optional)
              </Label>
              <p className="text-[11px] text-slate-500 -mt-1">
                A wide image shown at the top of your storefront. 16:9 works
                best — we auto-shrink big uploads.
              </p>
              {form.hero_image_url ? (
                <div className="relative rounded-md overflow-hidden border border-slate-200" style={{ aspectRatio: "16/9" }}>
                  <img src={form.hero_image_url} alt="hero" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, hero_image_url: "" })}
                    className="absolute top-2 right-2 text-xs bg-white/90 px-2 py-1 rounded hover:bg-white"
                    data-testid="brand-hero-clear"
                  >
                    Remove
                  </button>
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50 text-sm">
                  <Upload className="w-4 h-4" /> Upload
                  <input type="file" accept="image/*" className="hidden" onChange={uploadHero} data-testid="brand-hero-upload" />
                </label>
                <label className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50 text-sm" data-testid="brand-hero-camera-label">
                  <Camera className="w-4 h-4" /> Take photo
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadHero} data-testid="brand-hero-camera" />
                </label>
              </div>
              {form.hero_image_url && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Hero headline (optional, appears over the image)</Label>
                  <Input
                    value={form.hero_headline}
                    onChange={(e) => setForm({ ...form, hero_headline: e.target.value })}
                    placeholder="Fresh gadgets, delivered today"
                    maxLength={80}
                    data-testid="brand-hero-headline"
                  />
                </div>
              )}
            </div>

            <div className="space-y-3 border-t border-slate-100 pt-4">
              <Label className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Social & support links
              </Label>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1"><Instagram className="w-3.5 h-3.5 text-pink-600" /> Instagram URL</Label>
                  <Input
                    value={form.instagram_url}
                    onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                    placeholder="https://instagram.com/yourshop"
                    data-testid="brand-instagram"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1"><Facebook className="w-3.5 h-3.5 text-blue-600" /> Facebook URL</Label>
                  <Input
                    value={form.facebook_url}
                    onChange={(e) => setForm({ ...form, facebook_url: e.target.value })}
                    placeholder="https://facebook.com/yourshop"
                    data-testid="brand-facebook"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">WhatsApp support number</Label>
                  <Input
                    value={form.whatsapp_number}
                    inputMode="tel"
                    onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                    placeholder="+91 98765 43210"
                    data-testid="brand-whatsapp"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Support email</Label>
                  <Input
                    type="email"
                    value={form.support_email}
                    onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                    placeholder="hello@yourshop.com"
                    data-testid="brand-support-email"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2 border-t border-slate-100 pt-4">
              <Label className="flex items-center gap-1">
                <LinkIcon className="w-3.5 h-3.5" /> Public URL slug
              </Label>
              <div className="flex items-center gap-1 mono text-xs text-slate-500">
                <span className="text-slate-400">{window.location.origin}/shop/</span>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="my-shop"
                  maxLength={40}
                  className="mono text-sm"
                  data-testid="brand-slug"
                />
              </div>
              {form.slug && (
                <div
                  className={
                    "text-xs flex items-center gap-1 " +
                    (slugValid ? "text-emerald-700" : "text-red-600")
                  }
                  data-testid="brand-slug-status"
                >
                  {slugValid ? (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Will be saved as{" "}
                      <span className="mono">{slugPreview}</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3.5 h-3.5" />
                      Must be 3–40 chars, lowercase letters, numbers, or dashes.
                    </>
                  )}
                </div>
              )}
              <p className="text-[10px] text-slate-500">
                Share this link with customers — they land straight on YOUR branded storefront.
              </p>
              {savedSlug && !slugChanged && (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mono text-[11px] text-[#0055FF] hover:underline"
                  data-testid="brand-slug-open"
                >
                  {publicUrl} <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {savedSlug && !slugChanged && (
                <div className="pt-1.5 mt-1.5 border-t border-slate-100">
                  <p className="text-[10px] text-slate-500">
                    Same slug also opens your patient booking page, if Healthcare is enabled for your account:
                  </p>
                  <a
                    href={careUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mono text-[11px] text-[#0055FF] hover:underline"
                    data-testid="brand-care-link-open"
                  >
                    {careUrl} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <Label className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> Shop location
                <span className="text-slate-400 text-xs font-normal ml-1">
                  (powers the live delivery map for customers)
                </span>
              </Label>
              <Input
                value={form.shop_address}
                onChange={(e) => setForm({ ...form, shop_address: e.target.value })}
                placeholder="Full shop address"
                data-testid="brand-shop-address"
              />
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="any"
                  value={form.shop_lat}
                  onChange={(e) => setForm({ ...form, shop_lat: e.target.value })}
                  placeholder="Latitude"
                  className="mono text-sm"
                  data-testid="brand-shop-lat"
                />
                <Input
                  type="number"
                  step="any"
                  value={form.shop_lng}
                  onChange={(e) => setForm({ ...form, shop_lng: e.target.value })}
                  placeholder="Longitude"
                  className="mono text-sm"
                  data-testid="brand-shop-lng"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={useMyLocation}
                  className="whitespace-nowrap"
                  data-testid="brand-use-my-location"
                >
                  <MapPin className="w-3.5 h-3.5 mr-1" /> Use current
                </Button>
              </div>
              <p className="text-[10px] text-slate-500">
                Tap &ldquo;Use current&rdquo; from your shop&apos;s Wi-Fi/GPS, or paste values from Google Maps.
              </p>
            </div>

            <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="brand-policies">
              <div>
                <Label className="flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" /> Policy pages
                </Label>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Terms, Refund, Shipping, Privacy and Contact pages, linked at the bottom of your shop.
                  Payment gateways (Razorpay, PhonePe) check these before activating your account. They
                  are filled in from the details below; review them and change the wording if you need to.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Registered business name (as on PAN/GST)</Label>
                  <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                    placeholder={form.shop_name || "Computer Care"} data-testid="brand-legal-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Contact phone</Label>
                  <Input value={form.support_phone} inputMode="tel"
                    onChange={(e) => setForm({ ...form, support_phone: e.target.value })}
                    placeholder={form.whatsapp_number || "+91 98765 43210"} data-testid="brand-support-phone" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">GSTIN (optional)</Label>
                  <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                    placeholder="19ABCDE1234F1Z5" data-testid="brand-gstin" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Delivery time</Label>
                  <Input value={form.delivery_time} onChange={(e) => setForm({ ...form, delivery_time: e.target.value })}
                    placeholder="3-7 working days" data-testid="brand-delivery-time" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Days to report a damaged/wrong item</Label>
                  <Input type="number" min="0" value={form.refund_window_days}
                    onChange={(e) => setForm({ ...form, refund_window_days: e.target.value })}
                    placeholder="7" data-testid="brand-refund-days" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Refund sent within (working days)</Label>
                  <Input type="number" min="0" value={form.refund_processing_days}
                    onChange={(e) => setForm({ ...form, refund_processing_days: e.target.value })}
                    placeholder="7" data-testid="brand-refund-processing" />
                </div>
              </div>
              {!form.shop_address && (
                <p className="text-[11px] text-amber-700">
                  Add your full shop address under &ldquo;Shop location&rdquo; above: gateways need it on the Contact page.
                </p>
              )}
              <div className="rounded-md border border-slate-200 divide-y">
                {POLICIES.map((p) => (
                  <div key={p.key} className="p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm">
                        {p.label}
                        {p.key !== "contact" && form[`policy_${p.key}`] ? (
                          <span className="ml-2 text-[10px] uppercase text-emerald-700">your wording</span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <a
                          href={`/shop/${savedSlug ? `${savedSlug}/` : ""}policy/${p.key}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#0055FF] hover:underline"
                          data-testid={`brand-policy-view-${p.key}`}
                        >
                          View
                        </a>
                        {p.key !== "contact" && (
                          <button
                            type="button"
                            className="text-slate-600 hover:underline"
                            onClick={() => setEditPolicy(editPolicy === p.key ? null : p.key)}
                            data-testid={`brand-policy-edit-${p.key}`}
                          >
                            {editPolicy === p.key ? "Close" : "Edit wording"}
                          </button>
                        )}
                      </div>
                    </div>
                    {editPolicy === p.key && (
                      <div className="mt-2 space-y-1.5">
                        <Textarea
                          rows={10}
                          value={form[`policy_${p.key}`]}
                          onChange={(e) => setForm({ ...form, [`policy_${p.key}`]: e.target.value })}
                          placeholder="Leave empty to use the standard text."
                          data-testid={`brand-policy-text-${p.key}`}
                        />
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setForm({
                                ...form,
                                [`policy_${p.key}`]: defaultPolicy(p.key, form)
                                  .map(([h, t]) => `${h}\n${t}`)
                                  .join("\n\n"),
                              })
                            }
                          >
                            Start from standard text
                          </Button>
                          {form[`policy_${p.key}`] && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => setForm({ ...form, [`policy_${p.key}`]: "" })}
                            >
                              Use standard text
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-500">
                Standard text is a starting point, not legal advice. Remember to Save branding.
              </p>
            </div>

            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="brand-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save branding
            </Button>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-0 overflow-hidden">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500 p-4 pb-2">Live preview</div>
            <div className="border-t border-slate-100">
              <div
                className="px-4 py-3 flex items-center justify-between text-white"
                style={{ backgroundColor: form.primary_color }}
              >
                <div className="flex items-center gap-2">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="" className="w-6 h-6 rounded object-contain bg-white/10" />
                  ) : (
                    <ShoppingBag className="w-5 h-5" />
                  )}
                  <span className="font-heading font-bold">{form.shop_name || "DialFlow Shop"}</span>
                </div>
              </div>
              <div className="p-4">
                <div className="text-sm text-slate-600 italic">
                  {form.tagline || "Your tagline appears here — describe your shop in one line."}
                </div>
                <div className="mt-3">
                  <button
                    className="px-3 py-1.5 rounded-full text-sm text-white"
                    style={{ backgroundColor: form.primary_color }}
                  >
                    Sample button
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
